import { ConversationsData, Message } from '@nextforce/api'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { useConversations } from './conversations/queries/list'
import { useMessages } from './messages/queries/list'

interface RefetchMessagingContextValue {
  setShouldRefetchConversations: React.Dispatch<React.SetStateAction<boolean>>
}

interface Props {
  children: React.ReactNode
}

const RefetchMessagingContext = createContext(
  {} as RefetchMessagingContextValue
)

export function RefetchMessagingProvider(props: Props) {
  const conversationsQuery = useConversations()
  const messagesQuery = useMessages(data => data.messages)

  const shouldRefetchConversations = useRef(false)
  const retryCount = useRef(0)

  const setShouldRefetchConversations = useCallback(
    (value: boolean) => (shouldRefetchConversations.current = value),
    [shouldRefetchConversations]
  )

  useEffect(() => {
    if (
      shouldRefetchConversations.current &&
      !conversationsQuery.isFetching &&
      retryCount.current < 3
    ) {
      conversationsQuery
        .refetch()
        .then(() => {
          setShouldRefetchConversations(false)
          retryCount.current += 1
        })
        .catch(e => null)
    }
  }, [
    conversationsQuery,
    setShouldRefetchConversations,
    shouldRefetchConversations,
  ])

  const fetchConversationsIfNeeded = useCallback(
    (conversations: ConversationsData | undefined, messages: Message[]) => {
      const conversationIds = conversations?.map(
        conversation => conversation.id
      )
      const conversationDoesNotExist = messages.some(
        message => !conversationIds?.includes(message.conversationId)
      )
      if (conversationDoesNotExist) {
        setShouldRefetchConversations(true)
      }
    },
    [setShouldRefetchConversations]
  )

  useEffect(() => {
    if (
      messagesQuery.data &&
      conversationsQuery.data &&
      !messagesQuery.isRefetching &&
      !conversationsQuery.isRefetching
    ) {
      fetchConversationsIfNeeded(conversationsQuery.data, messagesQuery.data)
    }
  }, [
    conversationsQuery.data,
    conversationsQuery.isRefetching,
    fetchConversationsIfNeeded,
    messagesQuery.data,
    messagesQuery.isRefetching,
  ])

  const value: RefetchMessagingContextValue = {
    setShouldRefetchConversations,
  }
  return (
    <RefetchMessagingContext.Provider value={value}>
      {props.children}
    </RefetchMessagingContext.Provider>
  )
}

export function useRefetchMessagingContext() {
  return useContext<RefetchMessagingContextValue>(RefetchMessagingContext)
}
